import { tabBorder as tabBorderClassName, tabFloodlight as tabFloodlightClassName } from './tab.module.css';

import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';

import type { LinkProps } from '@tanstack/react-router';
import type { MouseEvent, ReactNode } from 'react';

export type TabProps = {
	id: string;
	isSelected: boolean;
	label: string;
	labelAdornEnd?: ReactNode;
	/** Forwarded to the underlying button or link as `data-testid`. */
	testId?: string;
	/** If set, the tab will render as a TanStack Router <Link> component. */
	linkProps?: Omit<LinkProps, 'children'>;
	/** If true, the tab renders muted and non-interactive (never as a link). */
	disabled?: boolean;
	onClick?: () => void;
	onContextMenu?: (event: MouseEvent<HTMLElement>) => void;
};

export function Tab({
	isSelected,
	label,
	labelAdornEnd,
	testId,
	linkProps,
	disabled,
	onClick,
	onContextMenu,
}: TabProps) {
	const commonProps = {
		className: clsx(
			'relative z-10 flex items-end rounded-t-sm px-3 py-2 align-bottom text-gray-a8 transition-colors duration-50',
			disabled ? 'text-gray-a6' : 'hover:bg-gray-a3',
			isSelected && 'text-gray-a12',
		),
		onContextMenu,
		onClick: disabled ? undefined : onClick,
		'data-testid': testId,
	};

	const children = (
		<div className='flex items-center gap-1.5'>
			<p>{label}</p>
			{labelAdornEnd !== null && labelAdornEnd !== undefined && labelAdornEnd !== false && (
				<div className='flex h-0 items-center gap-1.5'>{labelAdornEnd}</div>
			)}
		</div>
	);

	const element = useRender({
		props: linkProps && !disabled ? mergeProps(commonProps, linkProps) : commonProps,
		render:
			linkProps && !disabled ?
				(props) => (
					<Link {...(props as typeof commonProps & LinkProps)} data-block-save-on-click>
						{children}
					</Link>
				)
			:	(props) => (
					<button type='button' disabled={disabled} {...props}>
						{children}
					</button>
				),
	});

	return (
		<div className={clsx('relative grid w-max max-w-48 place-items-stretch', isSelected && tabBorderClassName)}>
			<div
				className={clsx(
					'absolute bottom-0 left-1/2 h-8 w-16 -translate-x-1/2 transition-opacity',
					tabFloodlightClassName,
					isSelected ? 'opacity-100 duration-100 ease-in' : 'opacity-0 duration-500 ease-out',
				)}
			/>
			{element}
		</div>
	);
}
